import React from "react";
import styled from "styled-components";
import { BackNav, PlayButton, SingleLineText } from "../common/CommonStyles";
import { FlashpackType, isInTrialMode, UnitWithLessons } from "../common/api";
import { LessonItem, LessonItemLoggingContext } from "../components/LessonItem";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ApiContext } from "../common/context/ApiContext";
import { useLoadingIndicator } from "../common/context/LoadingIndicatorContext";
import { Account, AccountContext } from "../common/context/AccountContext";
import { useLiveData } from "../common/utils";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBooks, faPlay } from "@fortawesome/pro-solid-svg-icons";
import { lessonCornerDecoration } from "../components/LessonItemBody";
import { CreateFlashpackItem } from "../components/flashpacks/CreateFlashpackItem";
import {
  AnalyticsContext,
  UnitHeaderPlayNextLessonButtonTapEvent,
  ViewUnitEvent,
} from "../common/context/AnalyticsContext";
import {
  getSectionsFromUnit,
  SectionType,
  SectionWithLessons,
} from "../common/SectionWithLessons";
import { TabEnum, TabsContext } from "../common/context/TabsContext";
import { COLOR_WHITE, COLOR_TEXT_PRIMARY } from "../common/colors";
import {
  playableUnitLessons,
  UnitDetailsLayout,
} from "../components/UnitDetails";

const FullCoursesButton = styled.button`
  background-color: ${COLOR_WHITE};
  border: 2px solid ${COLOR_TEXT_PRIMARY};
  border-radius: 8px;
  color: ${COLOR_TEXT_PRIMARY};
  cursor: pointer;
  padding: 8px 20px;
  margin: 20px auto;
  font-size: min(20px, 2rem);
  font-weight: 500;
`;

export const UnitPage: React.FC = () => {
  const history = useHistory();
  const { unitId }: { unitId: string } = useParams<{ unitId: string }>();
  const [isTeacherPD, setIsTeacherPD] = React.useState(false);
  const [unitWithLessons, setUnitWithLessons] =
    React.useState<UnitWithLessons | null>(null);
  const { getUnitWithLessons } = React.useContext(ApiContext);
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const { accountLiveData } = React.useContext(AccountContext);
  const unitWithLessonsFetchedForProfileStateVersion = React.useRef<
    number | null
  >(null);
  const fetchJobForProfileStateVersion = React.useRef<number | null>(null);
  const [account, setAccount] = React.useState<Account>();
  const [userProfileCanCreateFlashpacks, setUserProfileCanCreateFlashpacks] =
    React.useState(false);
  const { logEvent } = React.useContext(AnalyticsContext);
  const shouldLogViewUnit = React.useRef<boolean>(true);
  const [sections, setSections] = React.useState<SectionWithLessons[]>([]);
  const { setCurrentTab } = React.useContext(TabsContext);

  React.useEffect(() => {
    if (unitId === TabEnum.TeacherPD.split("/")[2]) {
      setIsTeacherPD(true);
      setCurrentTab(TabEnum.TeacherPD);
    }
  }, [setCurrentTab, unitId]);

  useLiveData(
    React.useCallback(
      async (account: Account) => {
        if (!unitId) {
          return;
        }
        if (account.hasMaybeProfileState()) {
          setAccount(account);
          if (
            account.profileStateCurrentVersion !==
              unitWithLessonsFetchedForProfileStateVersion.current &&
            account.profileStateCurrentVersion !==
              fetchJobForProfileStateVersion.current
          ) {
            startLoading();
            const expectedVersion = (fetchJobForProfileStateVersion.current =
              account.profileStateCurrentVersion);
            const unitWithLessonsResponse = await getUnitWithLessons(
              +unitId,
              account.profile?.id || null,
              account.profileState?.lessons_in_progress || {}
            );
            if (expectedVersion !== fetchJobForProfileStateVersion.current) {
              return;
            }
            // We only show sections that have lessons or "create custom flashpack" item.
            // Filter out everything else. If you decide to always show certain section even if it's empty,
            // this is the place to add that logic.
            setSections(
              getSectionsFromUnit(
                t,
                account.user,
                unitWithLessonsResponse.unit
              ).filter(
                (s) =>
                  s.lessons.length > 0 ||
                  s.type === SectionType.CustomFlashpacks
              )
            );
            setUnitWithLessons(unitWithLessonsResponse.unit);
            unitWithLessonsFetchedForProfileStateVersion.current =
              fetchJobForProfileStateVersion.current;
            setUserProfileCanCreateFlashpacks(
              account.hasUserAndMaybeProfile() &&
                account.hasNonGuestProfile() &&
                !isInTrialMode(account.user)
            );
            stopLoading();
          }
        }
      },
      [
        t,
        unitId,
        getUnitWithLessons,
        startLoading,
        stopLoading,
        setUserProfileCanCreateFlashpacks,
      ]
    ),
    accountLiveData
  );

  if (!account || !unitWithLessons) {
    return <></>;
  }

  if (shouldLogViewUnit.current) {
    logEvent(new ViewUnitEvent(+unitId, unitWithLessons.title));
    shouldLogViewUnit.current = false;
  }

  const unitLessons = playableUnitLessons(unitWithLessons);
  const unitState = account.profileState?.units_state?.[unitWithLessons.id];
  const completedCount = unitState?.completed_count ?? 0;
  const playLesson =
    unitWithLessons.flashpack_type == null
      ? (unitLessons.find(
          (lesson) => lesson.id === unitState?.next_lesson?.id
        ) ?? unitLessons[0])
      : undefined;
  const isPlayLessonStarted =
    completedCount > 0 ||
    (!!playLesson &&
      playLesson.id in (account.profileState?.lessons_in_progress || {}));

  return (
    <>
      {!isTeacherPD && <BackNav label={t("generic_nav_back")} />}
      <UnitDetailsLayout
        unit={unitWithLessons}
        completedCount={completedCount}
        sections={sections}
        headerAction={
          playLesson && (
            <PlayButton
              onClick={() => {
                logEvent(
                  new UnitHeaderPlayNextLessonButtonTapEvent(
                    unitWithLessons.id,
                    unitWithLessons.title,
                    playLesson.id,
                    playLesson.title
                  )
                );
                history.push(`/lessons/${playLesson.id}/${playLesson.slug}/`, {
                  unitId: unitWithLessons.id,
                });
              }}
            >
              <FontAwesomeIcon icon={faPlay} />
              <SingleLineText>
                {t(
                  isPlayLessonStarted
                    ? "unit_details_resume_lesson"
                    : "unit_details_start_lesson",
                  { title: playLesson.title }
                )}
              </SingleLineText>
            </PlayButton>
          )
        }
        renderSectionStart={(section) =>
          section.type === SectionType.CustomFlashpacks &&
          (unitWithLessons.flashpack_type === FlashpackType.Custom ||
            unitWithLessons.supports_flashpack_creation) &&
          userProfileCanCreateFlashpacks && (
            <CreateFlashpackItem
              flashpackType={unitWithLessons.flashpack_type!}
            />
          )
        }
        renderLesson={(lesson) => (
          <LessonItem
            unitId={+unitId}
            lesson={lesson}
            account={account}
            leftCornerDecoration={lessonCornerDecoration(lesson, account)}
            loggingContext={LessonItemLoggingContext.Lesson}
          />
        )}
        footer={
          unitWithLessons.is_sampler_unit && (
            <FullCoursesButton
              onClick={() => {
                history.push(TabEnum.Home);
              }}
            >
              <FontAwesomeIcon icon={faBooks} style={{ marginRight: "10px" }} />
              {t("unit_details_full_courses_button_title")}
            </FullCoursesButton>
          )
        }
      />
    </>
  );
};
